import React, { useState } from 'react';
import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import { useMutation } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import Callout from '@/dashboard/components/Callout';

const WebhookTestButton = ({ webhook }) => {
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const { sellerId, token } = useAuth();
  const { emitToast } = useToast();

  const testWebhook = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/webhooks/${webhook.id}/test`, token, {
        method: 'POST',
      }),
    onSuccess: data => {
      console.log(data);
      setTestResult(data);
      setShowResultDialog(true);
      if (data.success) {
        emitToast({
          title: 'Webhook test successful',
          description: `Response received in ${data.responseTime}ms`,
          type: 'success',
        });
      } else {
        emitToast({
          title: 'Webhook test failed',
          description: data.error || 'The endpoint did not respond successfully',
          type: 'error',
        });
      }
    },
    onError: error => {
      emitToast({
        title: 'Failed to test webhook',
        description: error?.message || 'Unable to send test request',
        type: 'error',
      });
    },
  });

  const handleTest = () => {
    testWebhook.mutate();
  };

  const getStatusColor = (success, httpStatus) => {
    if (success && httpStatus >= 200 && httpStatus < 300) {
      return 'text-green-600';
    }
    return 'text-red-600';
  };

  const formatResponseBody = responseBody => {
    if (!responseBody) return 'No response body';
    try {
      // Try to parse and format JSON
      const parsed = JSON.parse(responseBody);
      return JSON.stringify(parsed, null, 2);
    } catch {
      // Return as-is if not JSON
      return responseBody;
    }
  };

  const formatHeaders = headers => {
    if (!headers || Object.keys(headers).length === 0) {
      return 'No headers received';
    }
    return JSON.stringify(headers, null, 2);
  };

  return (
    <>
      <Button
        appearance="secondary"
        size="small"
        onClick={handleTest}
        disabled={testWebhook.isLoading}
      >
        {testWebhook.isLoading ? 'Testing...' : 'Test'}
      </Button>

      <Dialog
        title="Webhook test result"
        cancelButton={null}
        confirmButton={{
          label: 'Close',
          appearance: 'primary',
          onClick: () => setShowResultDialog(false),
        }}
        size="xl"
        open={showResultDialog}
        onCancel={() => setShowResultDialog(false)}
      >
        {testResult && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <div>
                <span className="font-medium text-gray-900">Status:</span>
                <span
                  className={`ml-2 font-medium ${getStatusColor(
                    testResult.success,
                    testResult.httpStatus,
                  )}`}
                >
                  {testResult.success ? 'Success' : 'Failed'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">HTTP Status:</span>
                <span
                  className={`ml-2 font-medium ${getStatusColor(
                    testResult.success,
                    testResult.httpStatus,
                  )}`}
                >
                  {testResult.httpStatus}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Response Time:</span>
                <span className="ml-2">{testResult.responseTime}ms</span>
              </div>

              <div>
                <span className="font-medium text-gray-700">Endpoint:</span>
                <span className="ml-2 text-gray-600 break-all">{webhook.url}</span>
              </div>
            </div>

            {testResult.error && (
              <Callout variant="error">
                <Callout.Heading>Error</Callout.Heading>
                <Callout.Content>
                  <code>{testResult.error}</code>
                </Callout.Content>
              </Callout>
            )}

            {testResult.responseBody && (
              <Callout variant="success">
                <Callout.Heading>Response body</Callout.Heading>
                <Callout.Content>
                  <pre className="w-full bg-gray-100 max-h-64 overflow-auto p-3 border rounded-lg text-sm whitespace-pre-wrap break-all">
                    {formatResponseBody(testResult.responseBody)}
                  </pre>
                </Callout.Content>
              </Callout>
            )}

            {testResult.headers && Object.keys(testResult.headers).length > 0 && (
              <Callout variant="success">
                <Callout.Heading>Response headers</Callout.Heading>
                <Callout.Content>
                  <pre className="w-full bg-gray-100 max-h-48 overflow-auto p-3 border rounded-lg text-sm whitespace-pre-wrap break-all">
                    {formatHeaders(testResult.headers)}
                  </pre>
                </Callout.Content>
              </Callout>
            )}

            <Callout variant="info">
              <Callout.Heading>About webhook testing</Callout.Heading>
              <Callout.Content>
                This test sends a sample payload to your webhook endpoint. A successful test means
                your endpoint is reachable and responding correctly. For production webhooks, ensure
                your endpoint validates the webhook signature and handles the actual event data
                structure.
              </Callout.Content>
            </Callout>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default WebhookTestButton;
